<script setup lang="ts">
import FinestInputText from '@/components/FinestInputText.vue'
import { useAddress } from '@/composables/useAddress'
import { useDebounceFn } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AddressData = App.Data.EstateObject.AddressData

defineOptions({ inheritAttrs: false })

defineProps<{
  placeholder?: string
}>()

const emit = defineEmits<{
  (event: 'select', address: AddressData): void
}>()

const { t } = useI18n()

const { search, suggestions, loading } = useAddress()

const street = defineModel<string | null>({ default: null })

const activeIndex = ref(-1)
const closed = ref(false)
const searchedQuery = ref('')

const query = computed(() => street.value ?? '')

const options = computed<AddressData[]>(() => suggestions.value as AddressData[])

const isOpen = computed(() => !closed.value && query.value.length >= 3)

const lookup = useDebounceFn(async () => {
  await search({ query: query.value })
  searchedQuery.value = query.value
}, 400)

const isEmptyResult = computed(
  () => isOpen.value && !loading.value && !options.value.length && searchedQuery.value === query.value,
)

const update = (value: string | null) => {
  street.value = value
  activeIndex.value = -1
  closed.value = false

  void lookup()
}

const choose = (address: AddressData) => {
  street.value = address.street ?? street.value
  closed.value = true
  activeIndex.value = -1

  emit('select', address)
}

const move = (offset: number) => {
  if (!options.value.length) {
    return
  }

  closed.value = false
  activeIndex.value = (activeIndex.value + offset + options.value.length) % options.value.length
}

const confirmActive = () => {
  const address = options.value[activeIndex.value]

  if (address) {
    choose(address)
  }
}
</script>

<template>
  <div class="relative">
    <FinestInputText
      v-bind="$attrs"
      :model-value="street"
      icon="pi pi-map-marker"
      :placeholder="placeholder ?? t('placeholder')"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="isOpen && options.length > 0"
      autocomplete="off"
      @update:model-value="update"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="confirmActive"
      @keydown.esc="closed = true"
      @blur="closed = true" />

    <i
      v-if="loading"
      class="pi pi-spinner text-action absolute top-1/2 right-3 -translate-y-1/2 animate-spin text-sm"
      aria-hidden="true" />

    <ul
      v-if="isOpen && options.length"
      class="border-line bg-surface absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border shadow-lg"
      role="listbox">
      <li
        v-for="(address, index) in options"
        :key="address.google_place_id ?? index"
        role="option"
        :aria-selected="index === activeIndex">
        <button
          type="button"
          class="hover:bg-info-subtle flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
          :class="index === activeIndex ? 'bg-info-subtle text-ink' : 'text-ink-2'"
          @mousedown.prevent="choose(address)"
          @mouseenter="activeIndex = index">
          <i class="pi pi-map-marker text-ink-3 text-xs" aria-hidden="true" />
          <span class="min-w-0 flex-1 truncate">{{ address.description }}</span>
        </button>
      </li>
    </ul>

    <p v-else-if="isEmptyResult" class="text-ink-3 mt-1.5 text-xs">{{ t('empty') }}</p>
  </div>
</template>

<i18n>
{
  "de": {
    "placeholder": "z. B. Lindenstraße 14, Mannheim",
    "empty": "Keine Adresse gefunden – tippe die Felder einfach selbst."
  },
  "en": {
    "placeholder": "e.g. Lindenstraße 14, Mannheim",
    "empty": "No address found – just fill in the fields yourself."
  }
}
</i18n>
